import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { logClientError } from "@/lib/errorLogger";
import { isChunkError, reloadForNewVersion } from "@/lib/lazyRetry";

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, reloading: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
    if (isChunkError(error)) {
      if (reloadForNewVersion()) { this.setState({ reloading: true }); return; }
    } else {
      try { logClientError("react_boundary", error, String(errorInfo?.componentStack || "").slice(0, 300)); } catch { /* never crash the boundary */ }
    }
  }

  render() {
    if (this.state.hasError) {
      const chunk = isChunkError(this.state.error);
      return (
        <div className="flex flex-col items-center justify-center min-h-[300px] gap-4 p-8" data-testid="error-boundary-fallback">
          {this.state.reloading ? <RefreshCw className="w-12 h-12 text-th-accent animate-spin" /> : <AlertTriangle className="w-12 h-12 text-amber-500" />}
          <h2 className="text-lg font-semibold text-[var(--th-text)]">{chunk ? "A new version is available" : "Something went wrong"}</h2>
          <p className="text-sm text-[var(--th-muted)] text-center max-w-md">
            {this.state.reloading ? "Reloading to get the latest version…" : chunk ? "The app was updated while this page was open. Reload to continue — nothing is lost." : (this.state.error?.message || "An unexpected error occurred. Please try refreshing.")}
          </p>
          {!this.state.reloading && (
            <Button
              onClick={() => { if (chunk) { sessionStorage.removeItem("chunk_reload_at"); window.location.reload(); } else this.setState({ hasError: false, error: null }); }}
              variant="outline"
              className="gap-2"
              data-testid="error-boundary-retry"
            >
              <RefreshCw className="w-4 h-4" />
              {chunk ? "Reload now" : "Try Again"}
            </Button>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}
